import { CircleX } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import PickerFieldMobile from '@/domains/mobile-web/components/PickerFieldMobile/PickerFieldMobile';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import BottomSheetMobile, {
    SheetOptionList,
    type SheetOption,
} from '@/domains/mobile-web/components/BottomSheetMobile/BottomSheetMobile';
import BankLogo from '@/domains/mobile-web/components/BankLogo/BankLogo';
import BottomAction from '@/domains/mobile-web/components/BottomAction/BottomAction';
import { fieldErrorOf } from '@/domains/mobile-web/hooks/useAddresses';
import { useBanks, useCreatePayoutAccount } from '@/domains/mobile-web/hooks/useBanks';
import type { Bank as BankItem } from '@/domains/mobile-web/types/bank.types';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';

type ErrorField = 'bank_code' | 'account_number';

const Bank = () => {
    const navigate = useNavigate();
    const banks = useBanks();
    const createAccount = useCreatePayoutAccount();
    const [isOpenSheet, setIsOpenSheet] = useState(false);
    const [selectedBank, setSelectedBank] = useState<BankItem | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [errors, setErrors] = useState<Partial<Record<ErrorField, string>>>({});
    const userName = useAuthStore((state) => state.name);

    // An e-wallet is paid to the phone number registered with it
    const numberLabel = selectedBank?.type === 'ewallet' ? 'Nomor Handphone' : 'Nomor Rekening';
    const bankOptions: SheetOption[] = (banks.data?.rows ?? [])
        .filter((bank) => bank.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map((bank) => ({ value: bank.code, label: bank.name }));

    const handleSelectBank = (option: SheetOption) => {
        setSelectedBank(banks.data?.rows.find((bank) => bank.code === option.value) ?? null);
        setErrors({});
        setIsOpenSheet(false);
    };

    const handleSubmit = () => {
        if (!selectedBank) return;
        createAccount.mutate(
            { bank_code: selectedBank.code, account_number: accountNumber.trim() },
            {
                // Back to Tarik Saldo or Affiliate, which list the new account
                onSuccess: () => navigate(-1),
                onError: (error) => {
                    const fieldError = fieldErrorOf(error);
                    if (fieldError && (fieldError.field === 'bank_code' || fieldError.field === 'account_number')) {
                        setErrors({ [fieldError.field]: fieldError.message });
                    } else {
                        showToast({ type: 'error', title: 'Gagal menambah rekening', message: error.message });
                    }
                },
            }
        );
    };

    const isFormValid = Boolean(selectedBank && accountNumber.trim().length > 0);
    // Shown from the shortest number the backend takes; not checked with DOKU, so the holder is the user
    const showPreview = selectedBank && accountNumber.replace(/\D/g, '').length >= 5;
    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader
                    type="back"
                    title="Tambah Rekening"
                    onBack={() => navigate(-1)}
                    onAdd={() => alert('tambah')}
                />
            </HeaderMobile>

            <WrapperMobile className='mb-[120px]'>
                <div className="w-full gap-3 flex flex-col shadowCard p-3 ">
                    <PickerFieldMobile
                        label="Pilih Bank"
                        name="bank"
                        placeholder="Pilih nama bank"
                        value={selectedBank?.name}
                        error={errors.bank_code}
                        onClick={() => setIsOpenSheet(true)}
                    />
                    <InputMobile
                        label={numberLabel}
                        name="nomor rekening"
                        placeholder={`isi ${numberLabel.toLowerCase()}`}
                        inputMode="numeric"
                        value={accountNumber}
                        error={errors.account_number}
                        onChange={(e) => {
                            setAccountNumber(e.target.value);
                            setErrors((prev) => ({ ...prev, account_number: undefined }));
                        }}
                    />

                </div>

                {showPreview && (
                    <div className="w-full gap-3 flex flex-col shadowCard p-3">
                        <p className="reg12 text-text-grey">Akun ditemukan</p>
                        <div className="w-full flex items-center gap-3 rounded-[12px] border border-blue-cs-200 px-4 py-2">
                            <BankLogo code={selectedBank.code} name={selectedBank.name} />
                            <div className="flex-1 min-w-0 text-black">
                                <p className="body1bold truncate">{userName || selectedBank.name}</p>
                                <p className="body1 truncate">{accountNumber.trim()}</p>
                            </div>
                            <button
                                type="button"
                                aria-label="Hapus nomor rekening"
                                className="text-text-grey cursor-pointer"
                                onClick={() => setAccountNumber('')}
                            >
                                <CircleX size={20} />
                            </button>
                        </div>
                    </div>
                )}
            </WrapperMobile>
            <BottomAction
                textButton='Konfirmasi dan Tambah'
                onClick={handleSubmit}
                disabled={!isFormValid || createAccount.isPending}
            />

            <BottomSheetMobile
                open={isOpenSheet}
                onClose={() => setIsOpenSheet(false)}
                title="Pilih Bank"
                searchable
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Cari nama bank"
            >
                {banks.isLoading ? (
                    <p className="body1 text-text-grey">Memuat...</p>
                ) : banks.isError ? (
                    <p className="body1 text-text-grey">Gagal memuat daftar bank.</p>
                ) : (
                    <SheetOptionList
                        options={bankOptions}
                        value={selectedBank?.code}
                        onSelect={handleSelectBank}
                    />
                )}
            </BottomSheetMobile>
        </main>
    );
};

export default Bank;
